import type { Channel as StreamChannel, StreamChat } from 'stream-chat';
import {
  CHAT_FILE_ID,
  counterpartOf,
  isEmployerSide,
  isJobBounded,
  jobIdFromChannel,
} from '@chat-emp/shared';

import { isBroadcast } from '../fixtures/rules.js';
import { cidParts } from '../lib/bridge.js';
import {
  BFF_CTA_ROUTES,
  hasButtons,
  isCtaButtonMode,
  isCtaButtonStyle,
  mayCarryCta,
} from './cta.js';
import { toFormNotice, toFormResponse } from './forms.js';
import { toJobCard } from './jobs.js';
import { isFirstReplyPending, SYSTEM_SENDER_ID } from './first-reply.js';
import { systemNoticeText } from './systemNotice.js';

import type {
  ApplicationStatus,
  Attachment,
  Channel,
  CtaButton,
  CtaPayload,
  JobMeta,
  Message,
  MessageStatus,
  Participant,
  VoiceMeta,
} from '../fixtures/domain.js';

/**
 * Stream → domain adapter. Maps a watched Stream channel to the exact domain
 * `Channel` (+ its counterpart `Participant`) the pure rules and components
 * already consume, so re-backing the inbox touches only the seam.
 *
 * Only STANDARD Stream fields are load-bearing here (members, last message,
 * `lastRead`) — the same data the old `stream-chat-react` list rendered. The
 * new UI's extra chrome (hiring stage, deletion clock, job meta) rides on custom
 * `channel.data` fields, read defensively: present → shown, absent → the badge
 * stays quiet, exactly as the old bare chat behaved. No fabricated values.
 */
export interface MappedChannel {
  channel: Channel;
  counterpart: Participant;
  /** The watched Stream channel handle — for reading messages, sending, markRead. */
  raw: StreamChannel;
}

/**
 * The SDK's own receipt index, kept current from the initial channel state and
 * the read and delivered events. Exported so a test double is checked against
 * the shape we actually call rather than drifting from it.
 */
export interface MessageReceiptsTracker {
  hasUserRead(ref: { timestampMs: number; msgId: string }, userId: string): boolean;
  hasUserDelivered(ref: { timestampMs: number; msgId: string }, userId: string): boolean;
}

/** The minimal slice of a watched Stream channel this adapter reads. */
interface ReadChannel {
  id?: string;
  cid: string;
  data?: Record<string, unknown>;
  countUnread(): number;
  /** The viewer's read watermark, or null when they have never opened it. Half
   *  of the unread mirror in `mapChannel` — the other half is `needsReply`. */
  lastRead(): Date | null | undefined;
  messageReceiptsTracker?: MessageReceiptsTracker;
  state: {
    last_message_at?: string | Date;
    members: Record<
      string,
      { user?: { id: string; name?: string; image?: string; online?: boolean } }
    >;
    messages: {
      id: string;
      text?: string;
      type?: string;
      /** Names which employer action a `system` note records. An open string:
       *  AJT ships new action types without a release here. See
       *  `systemNoticeText`. */
      custom_type?: string;
      user?: { id: string; name?: string };
      created_at?: string | Date;
      /** Set only when the text itself changed, unlike `updated_at`, which any reaction or seal bumps. */
      message_text_updated_at?: string | Date;
      /** Stream's own attachment array. `image_url` for images, `asset_url`
       *  otherwise — the shape `sendImage` / `sendFile` produce. */
      attachments?: {
        type?: string;
        image_url?: string;
        asset_url?: string;
        thumb_url?: string;
        /** Set only on scraped link previews — Stream's own URL enrichment of
         *  the message text, never on an upload. `toAttachments` drops on
         *  either, the same pair stream-chat-react's `isScrapedContent` reads. */
        title_link?: string;
        og_scrape_url?: string;
        /** `number | string` in Stream's own types, and both really occur: the
         *  React SDK writes `file.size` as a number, but the field is not
         *  normalised server-side, so any client may have written a string. */
        file_size?: number | string;
        title?: string;
        fallback?: string;
        /** Set on a file in our own bucket; its links are resolved per view. */
        file_id?: string;
      }[];
      /** Custom top-level field, authored upstream. Parsed by `toCta`. */
      cta?: unknown;
      /** Custom top-level fields for forms. Parsed by `toFormNotice` /
       *  `toFormResponse` in data/forms. */
      form?: unknown;
      form_response?: unknown;
      /** A job posting, parsed by `toJobCard` in data/jobs. */
      job?: unknown;
      /** Custom top-level flag stamped by the backend's auto-reply sender.
       *  Believed only on our own messages — see `mapMessages`. */
      auto_reply?: unknown;
      /**
       * Stream sends both this and an inlined `quoted_message` snapshot. Only
       * the id is kept: the snapshot froze when the reply was written, and the
       * thread already holds the current one.
       */
      quoted_message_id?: string;
    }[];
  };
}

/**
 * Whether this is a platform note rather than somebody talking.
 *
 * Either signal is enough, and the sender is the one that survives a partial
 * integration. The backend stamps both together — `SendSystemMessage` sets
 * `Type: "system"` and defaults an absent `user_id` to the sentinel in the same
 * call — but only when the caller omits `user_id`, and a caller that supplies
 * one silently lands on the ordinary path instead. Reading the sender as well
 * means a note that reaches us any other way still renders as nobody's message
 * rather than as a bubble from a phantom speaker.
 *
 * Safe to widen to, unlike `custom_type`: a member authors under their own id
 * and cannot write as the sentinel, so this is not a field a counterpart can
 * dress their own message up with. It is also the backend's own rule — see
 * `lastSpokenMessage` below, where pending-reply keys on the sender alone.
 */
function isSystemNote(m: { type?: string; user?: { id: string } }): boolean {
  return m.type === 'system' || m.user?.id === SYSTEM_SENDER_ID;
}

/**
 * The newest message somebody actually spoke, or undefined if nobody has.
 *
 * The sender, never the type: the backend's pending-reply fold skips
 * `senderUserID == "system"` and never reads the type, so keying on the same
 * field keeps the unreplied dot and the server's `awaiting_reply` in lockstep
 * — even for a note typed "system" but sent under a real user id, which the
 * backend counts as that user speaking.
 */
function lastSpokenMessage(
  msgs: ReadChannel['state']['messages'],
): ReadChannel['state']['messages'][number] | undefined {
  // Backwards loop, not `findLast`: that is ES2023 and this repo's lib is ES2022.
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i]!;
    if (m.user?.id !== SYSTEM_SENDER_ID) return m;
  }
  return undefined;
}

/**
 * Whether this thread is a platform announcement rather than a conversation
 * with a person, asked of the raw channel.
 *
 * Wrapped so `mapChannel` and `mapMessages` put the same question to the same
 * fields. The CTA gate depends on the answer, and a second spelling of it is
 * exactly the kind that drifts into admitting a forged row.
 */
function isBroadcastChannel(c: ReadChannel): boolean {
  const source = c.data?.source;
  return isBroadcast({
    memberIds: Object.keys(c.state.members),
    source: typeof source === 'string' ? source : undefined,
  });
}

/** Per bare channel id: whether this employer has unlocked its candidate. */
const unlockHints = new Map<string, boolean>();

/** Record a page's answer. Absent `unlocked` is "unknown" and leaves the page alone. */
export function absorbUnlocks(pageIds: string[], unlocked: string[] | undefined): void {
  if (!unlocked) return;
  const set = new Set(unlocked);
  for (const id of pageIds) unlockHints.set(cidParts(id).channelId, set.has(id));
}

export function setUnlockHint(channelId: string, unlocked: boolean): void {
  unlockHints.set(cidParts(channelId).channelId, unlocked);
}

/**
 * `flagged` is passed in rather than read off the channel. The assistant's
 * verdict used to arrive as `assistant_flag` in Stream custom data and must
 * never do so again: custom data reaches every member of a channel, and one of
 * them is the person the verdict is about. It comes with the inbox page now, on
 * an employer-scoped route (`InboxQueryResult.assistant_flagged`).
 *
 * Callers with no page in hand pass nothing and get no mark, which is why the
 * live re-derive in `data/inbox.ts` carries the page's answer forward rather
 * than letting a new message retire a verdict nothing has withdrawn.
 */
export function mapChannel(sc: StreamChannel, userId: string, flagged = false): MappedChannel {
  const c = sc as unknown as ReadChannel;

  const memberIds = Object.keys(c.state.members);
  const counterpartId = counterpartOf(memberIds, userId);
  const cp = c.state.members[counterpartId]?.user;

  const msgs = c.state.messages;
  const last = msgs.length > 0 ? msgs[msgs.length - 1] : undefined;
  const lastAttachments = toAttachments(last?.attachments);
  const lastSenderId = last?.user?.id ?? userId;
  // Falls back to the channel's creation time so a channel nobody has spoken in
  // yet still sorts somewhere stable — the read model orders those by creation
  // too (AI-2664). Without it the `new Date()` below re-dates the row on every
  // render, churning it to the top of the inbox instead of letting it settle.
  const createdAt = c.data?.created_at;
  const at =
    c.state.last_message_at ??
    last?.created_at ??
    (typeof createdAt === 'string' || createdAt instanceof Date ? createdAt : undefined);
  // Reads past system notes, the way the backend's fold does: a note landing
  // after my reply must not re-open a debt I already paid, and one landing on top
  // of the candidate's message must not clear a debt I still owe. Only the debt
  // skips them — the preview line and sort key stay on the true last message.
  const spoken = lastSpokenMessage(msgs);
  const needsReply = !!spoken && !isEmployerSide(spoken.user?.id ?? userId);

  /**
   * Unread, mirrored from the read model rather than read off Stream.
   *
   * The BFF owns this definition — `awaiting_reply = TRUE AND (last_read_at IS
   * NULL OR last_read_at < last_message_at)` — because the inbox's Unread pill
   * is a server-side COUNT over exactly that. The list is paginated, so the
   * client never holds the whole set and cannot count it. Every other unread
   * surface therefore has to answer the same question the same way, or the pill
   * and the rows under it disagree about the same channel.
   *
   * `countUnread()` is the one that cannot. Stream counts a system note — it is
   * nobody's own message and is not sent `silent` — while the read model's fold
   * skips the sentinel sender outright. So an end-chat notice landing on a chat
   * the employer had already read lit the row dot while the pill stayed put.
   *
   * The two halves here are the two the clause names: `needsReply` is
   * `awaiting_reply` (already keyed on the sender, for exactly this reason), and
   * the watermark comparison is Stream's own read state — which is where the
   * backend's copy of it comes from in the first place.
   *
   * Undatable falls to unread, matching the NULL watermark upstream: over-report
   * and let a real read clear it, rather than hide something that arrived.
   *
   * Info threads are NOT excluded, unlike `unrepliedCount` below: the read model
   * projects a marketing row carrying its own `awaiting_reply` (see
   * `syncMarketingRow`), so excluding them here would hide from the rail what
   * the pill still counts.
   */
  const lastRead = c.lastRead();
  const spokenAt = spoken?.created_at ? new Date(spoken.created_at) : undefined;
  const unread = needsReply && (!lastRead || !spokenAt || spokenAt > lastRead);

  const data = c.data ?? {};

  const source = typeof data.source === 'string' ? data.source : undefined;
  const info = isBroadcastChannel(c);

  const meta = (data as { meta?: { job_title?: string; job_location?: string } }).meta;
  const jobId = jobIdFromChannel({ id: c.cid, data });
  const jobMeta: JobMeta | undefined =
    isJobBounded({ id: c.cid, data }) && jobId
      ? {
          jobId,
          jobTitle: meta?.job_title ?? '',
          jobType: '', // not on the channel — the details route supplies it
          ...(meta?.job_location ? { jobLocation: meta.job_location } : {}),
        }
      : undefined;

  const channel: Channel = {
    id: c.cid,
    memberIds,
    counterpartId,
    // Through the same rewrite as the thread: an action notice previews in the
    // employer's words in the inbox too, or the two disagree about the message
    // directly under each other.
    lastMessageText:
      last && isSystemNote(last)
        ? systemNoticeText(last.custom_type, last.text ?? '')
        : // A form sent with no note previews as the form's name.
          last?.text || toFormNotice(last?.form)?.name || toJobCard(last?.job)?.title || '',
    ...(lastAttachments.length > 0 ? { lastMessageHasAttachment: true } : {}),
    ...(lastAttachments.some((a) => a.voice) ? { lastMessageIsVoice: true } : {}),
    lastSenderId,
    lastActivityAt: at ? new Date(at).toISOString() : new Date().toISOString(),
    // 0/1, not a tally: the read model answers "is there something unread", and
    // every in-app consumer of this field only ever asks `> 0` (the row dot, the
    // rail dots, the saved list's own pill). A number Stream could supply and the
    // BFF could not would be a number the pill can never agree with.
    unreadCount: unread ? 1 : 0,
    // The tally, kept for the one consumer that is not this app: the
    // `channel-select` host event, whose payload is documented as a count in
    // parity with `@ajt/stream-chat`. See `Channel.unreadMessages` — it is
    // Stream's answer on purpose, because that is the number the component this
    // event mirrors emitted, system notices and all.
    unreadMessages: c.countUnread(),
    // ponytail: no custom "unreplied" field yet, so approximate from the last
    // sender — one open thread the employer still owes. An exact count needs a
    // backend field (deferred with the rest of the custom chrome).
    // Never for an Info thread: the platform is always its last sender, so the
    // approximation would show a permanent debt nothing can discharge.
    unrepliedCount: needsReply && !info ? 1 : 0,
    // Real state, from the tail already in hand — see first-reply.ts. Stream
    // returns a bounded window, so a candidate message older than it reads as
    // "no candidate message" and the nudge stays hidden: failing closed, never
    // promising credits twice.
    firstReplyPending: isFirstReplyPending(msgs, userId),
    // Only when true. Absent and false are the same fact to every reader — no
    // mark — and carrying the false would put a key on every row in the inbox
    // to say nothing.
    ...(flagged ? { assistantFlag: true } : {}),
    ...(data.delivery === 'email' ? { byEmail: true } : {}),
    phase: data.status === 'closing' || data.status === 'pending_delete' ? data.status : 'open',
    ...(data.delete_at ? { deleteAt: String(data.delete_at) } : {}),
    ...(data.inactivity_delete_at ? { inactivityDeleteAt: String(data.inactivity_delete_at) } : {}),
    ...(data.applicationStatus
      ? { applicationStatus: data.applicationStatus as ApplicationStatus }
      : {}),
    ...(source ? { source } : {}),
    ...(jobMeta ? { jobMeta } : {}),
  };

  const counterpart: Participant = {
    id: counterpartId,
    name: cp?.name ?? 'Unknown',
    role: 'jobseeker',
    // Presence is a fact about a person, so a marketing persona reports none
    // rather than "Offline".
    ...(info ? {} : { online: Boolean(cp?.online) }),
    ...(cp?.image ? { avatarUrl: cp.image } : {}),
  };
  const unlocked = unlockHints.get(c.id ?? '');
  if (!info && unlocked !== undefined) counterpart.locked = !unlocked;

  return { channel, counterpart, raw: sc };
}

/** Map a watched channel's messages to domain `Message`s (oldest → newest). */
export function mapMessages(sc: StreamChannel, userId: string): Message[] {
  const c = sc as unknown as ReadChannel;
  const receiptFor = receiptReader(c, userId);
  // The CTA forgery gate — the rule and the threat live with `mayCarryCta`.
  // It runs here because this is the choke point: nothing upstream strips the
  // field (no `before_message_send` hook exists, and the backend carries it
  // through as an opaque map).
  const broadcast = isBroadcastChannel(c);
  return c.state.messages.map((m) => {
    const at = m.created_at ? new Date(m.created_at) : new Date();
    const senderId = m.user?.id ?? '';
    const cta = mayCarryCta({ broadcast, senderId, sessionUserId: userId })
      ? toCta(m.cta)
      : undefined;
    const form = toFormNotice(m.form);
    const formResponse = toFormResponse(m.form_response);
    // Gated like the CTA: `job` carries a client-set verified badge and a remote logo url, so a
    // candidate's forged one would render a "Verified employer" card and fetch an attacker url.
    const job = isEmployerSide(senderId) ? toJobCard(m.job) : undefined;
    // Believed only on our own message, for the same reason `mayCarryCta`
    // gates the CTA: the field is client-settable, nothing upstream strips it,
    // and this is the choke point. The backend stamps it only on a message it
    // sent AS the account, so a marker on the candidate's message can only be
    // forged — and a forged one would label their words "Automatic reply",
    // which is the whole value of the label inverted.
    const autoReply = isEmployerSide(senderId) && m.auto_reply === true;
    const attachments = toAttachments(m.attachments);
    const system = isSystemNote(m);
    return {
      id: m.id,
      channelId: c.cid,
      senderId,
      ...(m.user?.name ? { senderName: m.user.name } : {}),
      // Only a system note is rewritten. Everything else is a person's own
      // words, which we render as written.
      text: system ? systemNoticeText(m.custom_type, m.text ?? '') : (m.text ?? ''),
      createdAt: at.toISOString(),
      ...(m.message_text_updated_at
        ? { editedAt: new Date(m.message_text_updated_at).toISOString() }
        : {}),
      status: senderId === userId ? receiptFor(m.id, at) : 'sent',
      system,
      ...(cta ? { cta } : {}),
      ...(form ? { form } : {}),
      ...(formResponse ? { formResponse } : {}),
      ...(job ? { job } : {}),
      ...(autoReply ? { autoReply: true } : {}),
      ...(attachments.length > 0 ? { attachments } : {}),
      ...(m.quoted_message_id ? { quotedMessageId: m.quoted_message_id } : {}),
      // Gated on `system` for the reason `mayCarryCta` gates the CTA:
      // `custom_type` is client-settable and nothing upstream strips it, so it
      // is only believed on a note already established as the platform's own.
      // A counterpart cannot author under the sentinel, so they cannot dress
      // their own message up as one.
      ...(system && m.custom_type ? { notice: m.custom_type } : {}),
    };
  });
}

/**
 * Whether a url is safe to put in an `href` or `src`, or to fetch — CTA button
 * urls go through this too (`toCtaButton`).
 *
 * Attachments on an INBOUND message are authored by the sender, so nothing in
 * `attachment-rules.ts` constrains them — that guards our own upload path only.
 * This is the same http(s)-only posture the repo already applies to links (see
 * `MessageMarkdown`, where a rejected protocol arrives with no href and the link
 * stays text); attachments were bypassing it entirely.
 *
 * Dropping rather than rewriting, unlike `stream-chat-react`'s `SafeAnchor`
 * which sanitises to `about:blank`: an attachment we will not link to has
 * nothing left to render, and a dead anchor is worse than no row.
 */
function isRenderableUrl(url: string): boolean {
  try {
    // Parsed with no base on purpose. A base would make a relative path — or
    // plain garbage — resolve against our own origin and pass as http, so
    // `not a url at all` became `http://localhost/not%20a%20url…`. Stream's CDN
    // always hands back an absolute url, so anything relative is not ours.
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Stream attachments to the domain shape.
 *
 * A scraped link preview is dropped whole. It is Stream's enrichment of a url
 * in the TEXT, not something the sender attached, yet it carries an `image_url`
 * (the page's OG image) that used to pass the filter below and render as a
 * picture nobody sent. Worse than the stray logo: an attachment SIZES its
 * bubble (see `.hasAttachment` in MessageBubble.module.css), and a preview
 * image with no width yet collapsed the whole message to a one-character-wide
 * column (AI-2821).
 *
 * An entry is also dropped when it has no usable url, or one we will not
 * render: giphy cards travel through this array too, and a bubble with a
 * broken image is worse than one without it.
 */
function toAttachments(raw: ReadChannel['state']['messages'][number]['attachments']): Attachment[] {
  if (!Array.isArray(raw)) return [];
  const out: Attachment[] = [];
  for (const a of raw) {
    if (a.og_scrape_url || a.title_link) continue;
    const isImage = a.type === 'image' || Boolean(a.image_url);
    const fileId =
      typeof a.file_id === 'string' && CHAT_FILE_ID.test(a.file_id) ? a.file_id : undefined;
    const linked = isImage ? (a.image_url ?? a.thumb_url) : a.asset_url;
    const url = !fileId && linked && isRenderableUrl(linked) ? linked : undefined;
    if (!url && !fileId) continue;
    // On an image `thumb_url` is already the fallback for `image_url` above, so
    // carrying it again would just repeat the url. On anything else it is the
    // rendered first page, and this is the only place it exists.
    // `parseFloat`, not `Number`: `Number('')` is 0, which would report an empty
    // string as a zero-byte file rather than as no size at all.
    const size = typeof a.file_size === 'string' ? Number.parseFloat(a.file_size) : a.file_size;
    const sizeBytes =
      typeof size === 'number' && Number.isFinite(size) && size >= 0 ? size : undefined;
    const thumbUrl =
      !fileId && !isImage && a.thumb_url && isRenderableUrl(a.thumb_url) ? a.thumb_url : undefined;
    const voice = isImage ? undefined : toVoice(a);
    out.push({
      type: isImage ? 'image' : 'file',
      url: url ?? '',
      name: a.title ?? a.fallback ?? (voice ? 'Voice note' : isImage ? 'Image' : 'File'),
      ...(sizeBytes !== undefined ? { sizeBytes } : {}),
      ...(thumbUrl ? { thumbUrl } : {}),
      ...(fileId ? { fileId } : {}),
      ...(voice ? { voice } : {}),
    });
  }
  return out;
}

/** Keyed on Stream's `voiceRecording`, or any audio file, which plays the same way. */
function toVoice(a: Record<string, unknown>): VoiceMeta | undefined {
  const audio = typeof a.mime_type === 'string' && a.mime_type.startsWith('audio/');
  if (a.type !== 'voiceRecording' && !audio) return undefined;
  const duration = Number(a.duration);
  const waveform = Array.isArray(a.waveform_data)
    ? a.waveform_data.filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
    : [];
  return {
    durationS: Number.isFinite(duration) && duration > 0 ? duration : 0,
    waveform: waveform.map((v) => Math.min(Math.max(v, 0), 1)),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Parse the wire `cta` into the domain payload, snake_case → camelCase.
 *
 * Defensive throughout: nothing upstream validates this field before it reaches
 * us (the backend passes it through as an opaque map), so a malformed row has to
 * degrade rather than throw. `completed` is read strictly — anything that is not
 * literally `true` leaves the row live, because wrongly greying a live offer is
 * worse than wrongly offering a spent one, which the backend's own re-check
 * catches.
 */
function toCta(source: unknown): CtaPayload | undefined {
  if (!isRecord(source)) return undefined;

  const buttons = Array.isArray(source.buttons)
    ? source.buttons.map(toCtaButton).filter((button): button is CtaButton => button !== null)
    : [];

  const cta: CtaPayload = {
    buttons,
    completed: source.completed === true,
    ...(typeof source.completed_action_id === 'string'
      ? { completedActionId: source.completed_action_id }
      : {}),
    ...(typeof source.completed_at === 'string' ? { completedAt: source.completed_at } : {}),
    ...(Array.isArray(source.exclude_user_ids)
      ? { excludeUserIds: source.exclude_user_ids.filter((id) => typeof id === 'string') }
      : {}),
  };

  // The domain owns "an empty button array is not a row", so a payload whose
  // buttons all failed to parse is rejected by the same rule the renderer uses
  // rather than by a second copy of it here.
  return hasButtons(cta) ? cta : undefined;
}

/** A button with no id or no label cannot be clicked or read. It is dropped. */
function toCtaButton(source: unknown): CtaButton | null {
  if (!isRecord(source)) return null;
  const { id, label } = source;
  if (typeof id !== 'string' || id === '' || typeof label !== 'string') return null;

  return {
    id,
    label,
    // An unknown mode falls back to "event", the mode that does nothing on its
    // own: it asks the host page, and a host that does not recognise the button
    // simply never answers. The other two act unilaterally.
    mode: isCtaButtonMode(source.mode) ? source.mode : 'event',
    ...(isCtaButtonStyle(source.style) ? { style: source.style } : {}),
    // Same http(s)-only posture as `toAttachments`, plus this BFF's own exact
    // routes (a Chat Day invite's Accept / Decline): those are relative by
    // design, and stripping them sent the tap down the plain cta-click path.
    // Stripped rather than dropping the button, because a url-mode button with
    // no url already bails cleanly in `useMessageCta`.
    ...(typeof source.url === 'string' &&
    (isRenderableUrl(source.url) || BFF_CTA_ROUTES.has(source.url))
      ? { url: source.url }
      : {}),
    ...(source.method === 'PUT' || source.method === 'POST' ? { method: source.method } : {}),
    ...(typeof source.reply === 'string' ? { reply: source.reply } : {}),
    ...(isRecord(source.payload) ? { payload: source.payload } : {}),
  };
}

/**
 * `sent` is the answer whenever the channel cannot say better: Stream withholds
 * delivery unless the channel type has delivery events enabled, and a candidate
 * can switch read receipts off entirely. An unanswered question reads as "not
 * read yet" rather than as a wrong claim.
 */
function receiptReader(c: ReadChannel, userId: string): (id: string, at: Date) => MessageStatus {
  const tracker = c.messageReceiptsTracker;
  if (!tracker) return () => 'sent';
  const counterpartId = counterpartOf(Object.keys(c.state.members), userId);
  return (id, at) => {
    const ref = { timestampMs: at.getTime(), msgId: id };
    if (tracker.hasUserRead(ref, counterpartId)) return 'read';
    return tracker.hasUserDelivered(ref, counterpartId) ? 'delivered' : 'sent';
  };
}

/** Query the session user's channels (watched) and map them to domain shapes. */
export async function queryUserChannels(client: StreamChat): Promise<MappedChannel[]> {
  const userId = client.userID;
  if (!userId) return [];
  const channels = await client.queryChannels(
    { members: { $in: [userId] } },
    { last_message_at: -1 },
    // `presence` — see hydrateChannels.
    { watch: true, state: true, presence: true },
  );
  return channels.map((c) => mapChannel(c, userId));
}

/**
 * Hydrate one page of read-model ids into watched Stream channels.
 *
 * Two things this has to get right:
 *
 * 1. **Ids, not cids.** The read model stores Stream's bare channel id
 *    (`job-77-abc`); our domain `Channel.id` is the cid (`messaging:job-77-abc`).
 *    The `$in` filter takes the bare form, so results are matched back on
 *    `channel.id`.
 * 2. **Order.** Stream's `$in` ignores sort, so the backend's keyset ordering
 *    survives only by mapping results back onto `pageIds`. Ids that came back
 *    empty (deleted between the query and the hydrate) are dropped rather than
 *    left as holes.
 *
 * `watch: true` is what makes the page live. Nothing unwatches: watching is
 * state on the one WebSocket `connectUser` opened, and `disconnectUser` (called
 * when chat closes) ends every watch server-side in a single step. Per-channel
 * `stopWatching` costs one HTTP request each, and a page-sized burst of them
 * (leaving the inbox) tripped Stream's per-endpoint rate limit — see AI-2550.
 *
 * `presence: true` is what makes the online dot live. Stream defaults it off,
 * and off means `user.presence.changed` never arrives: the dot froze at
 * whatever was true when the page loaded. With it on, the SDK patches the
 * member's `user.online` in place and the re-map on that event picks it up
 * (WATCHED_EVENTS in inbox.ts for the row, LIVE_EVENTS in source.ts for the
 * header).
 */
export async function hydrateChannels(
  client: StreamChat,
  pageIds: string[],
  /** Bare channel ids the assistant flagged, from the same page response. */
  flagged?: ReadonlySet<string>,
): Promise<MappedChannel[]> {
  const userId = client.userID;
  if (!userId || pageIds.length === 0) return [];

  const channels = await client.queryChannels(
    // `members` is not a narrowing convenience — it is what keeps one channel
    // from blanking the whole inbox. Stream refuses the ENTIRE query with 403
    // (code 70, "N channels match your query but cannot be returned because you
    // don't have access to them") when a single id in `$in` names a channel the
    // session user is not a member of *in Stream*. Our read model is the source
    // of the ids and it can disagree with Stream — a member add that half
    // landed, a channel re-keyed — and when it does, the employer sees
    // "Couldn't load conversations" for every conversation they have, not just
    // the one that drifted. With the filter, the drifted channel simply does
    // not come back and the page renders; `byId` below already drops ids that
    // return nothing. queryUserChannels has always filtered this way.
    { id: { $in: pageIds }, members: { $in: [userId] } },
    [],
    // limit must cover the page: Stream's default would silently truncate it.
    { watch: true, state: true, presence: true, limit: pageIds.length },
  );

  const byId = new Map<string, StreamChannel>();
  for (const c of channels) {
    const id = (c as unknown as ReadChannel).id;
    if (id) byId.set(id, c);
  }

  const ordered = pageIds
    .map((id) => byId.get(id))
    .filter((c): c is StreamChannel => c !== undefined);
  // Both sides speak the bare Stream id here — `pageIds` is what the read model
  // returned and what `byId` is keyed by — so the two need no reconciling.
  return ordered.map((c) =>
    mapChannel(c, userId, flagged?.has((c as unknown as ReadChannel).id ?? '') === true),
  );
}
